import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { forgetPreferencesHere } from "@/features/preferences/store";

import { AuthContext, type AuthContextValue } from "./context";
import { clearSession, loadSession, saveSession } from "./session-storage";
import {
  type AuthApi,
  type AuthState,
  type Credentials,
  hasPermission,
  type Session,
} from "./types";

interface AuthProviderProps {
  api: AuthApi;
  children: ReactNode;
  /** Injected in tests; defaults to window.sessionStorage. */
  storage?: Storage;
  /** Refresh this long before the access token expires (ms). */
  refreshLeadMs?: number;
}

/** Access tokens are short (15 min at identity-service); refresh a minute before they run out. */
const DEFAULT_REFRESH_LEAD_MS = 60_000;

function initialState(storage: Storage): AuthState {
  const existing = loadSession(storage);
  // No stored session → "loading" while the refresh cookie is tried (see the effect below).
  return existing ? { status: "authenticated", session: existing } : { status: "loading" };
}

function sessionOf(state: AuthState): Session | null {
  return state.status === "authenticated" || state.status === "expired" ? state.session : null;
}

/**
 * Staff session (T11-02, design/08 §1, design/11 §2): the access JWT lives in memory; the refresh
 * token is an HttpOnly cookie identity-service rotates. The provider restores the session on
 * start, refreshes silently before expiry and — when a refresh fails — switches to "expired"
 * instead of dropping the screen, so whatever the staff member was typing stays on the page (and
 * in its draft) under the "please sign in again" prompt.
 */
export function AuthProvider({
  api,
  children,
  storage,
  refreshLeadMs = DEFAULT_REFRESH_LEAD_MS,
}: AuthProviderProps) {
  const store = storage ?? window.sessionStorage;
  const [state, setState] = useState<AuthState>(() => initialState(store));
  const sessionRef = useRef<Session | null>(sessionOf(state));
  const inflight = useRef<Promise<string | null> | null>(null);

  const commit = useCallback(
    (next: AuthState) => {
      const session = sessionOf(next);
      sessionRef.current = next.status === "authenticated" ? session : null;
      if (next.status === "authenticated" && api.persistSession) {
        saveSession(next.session, store);
      } else if (next.status !== "authenticated") {
        clearSession(store);
      }
      setState(next);
    },
    [api, store],
  );

  const refreshNow = useCallback((): Promise<string | null> => {
    if (!inflight.current) {
      const previous = sessionRef.current;
      inflight.current = api
        .refresh()
        .catch(() => null)
        .then((fresh) => {
          if (fresh) {
            commit({ status: "authenticated", session: fresh });
            return fresh.accessToken;
          }
          commit(previous ? { status: "expired", session: previous } : { status: "anonymous" });
          return null;
        })
        .finally(() => {
          inflight.current = null;
        });
    }
    return inflight.current;
  }, [api, commit]);

  // Start: no stored session → try the refresh cookie once (reload of an open admin tab).
  const started = useRef(false);
  useEffect(() => {
    if (started.current || state.status !== "loading") {
      return;
    }
    started.current = true;
    void api
      .refresh()
      .catch(() => null)
      .then((fresh) => {
        commit(fresh ? { status: "authenticated", session: fresh } : { status: "anonymous" });
      });
  }, [api, commit, state.status]);

  // Silent refresh shortly before the access token runs out.
  useEffect(() => {
    if (state.status !== "authenticated") {
      return;
    }
    const due = state.session.expiresAt - Date.now() - refreshLeadMs;
    const timer = setTimeout(
      () => {
        void refreshNow();
      },
      Math.max(due, 0),
    );
    return () => clearTimeout(timer);
  }, [state, refreshNow, refreshLeadMs]);

  const signIn = useCallback(
    async (credentials: Credentials) => {
      const step = await api.signIn(credentials);
      if (step.kind === "session") {
        commit({ status: "authenticated", session: step.session });
      }
      return step;
    },
    [api, commit],
  );

  const acceptSession = useCallback(
    (session: Session) => {
      commit({ status: "authenticated", session });
    },
    [commit],
  );

  const signOut = useCallback(async () => {
    const current = sessionOf(state);
    if (current) {
      // saved views may hold customer names: gone from this (shared) computer (T01-17 review)
      await forgetPreferencesHere(current.user.id);
      await api.signOut(current).catch(() => undefined);
    }
    commit({ status: "anonymous" });
  }, [api, commit, state]);

  const value = useMemo<AuthContextValue>(() => {
    const session = sessionOf(state);
    return {
      state,
      session,
      api,
      signIn,
      acceptSession,
      signOut,
      can: (permission) => hasPermission(session, permission),
      getAccessToken: () => sessionRef.current?.accessToken ?? null,
      refreshNow,
    };
  }, [state, api, signIn, acceptSession, signOut, refreshNow]);

  return <AuthContext value={value}>{children}</AuthContext>;
}
